// ---------------------------------------------------------------------------
// Operations guides - keeping the building working.
//
// This lane is about the physical place rather than the money or the people:
// what breaks, what you own, what you check, and what it all costs.
//
// Route note: the expenses lane deliberately never routes to /expenses/dashboard
// or /expenses/budgets. Those two are gated on `financials.view`, not
// `expenses.view`, so an ops user with full expense permissions still gets a 403
// on them. /expenses/all is the honest home for this guide.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';

const SR_TICKETS = PATH_ADMIN.serviceRequests.tickets;
const SR_TEAMS = PATH_ADMIN.serviceRequests.teams;
const SR_CATEGORY = PATH_ADMIN.serviceRequests.category;
const ASSETS = PATH_ADMIN.assetManagement.root;
const ASSETS_MAINTENANCE = PATH_ADMIN.assetManagement.maintenance;
const INSPECT_TEMPLATES = PATH_ADMIN.inspectAdmin.setup;
const INSPECT_RECORDS = PATH_ADMIN.inspectAdmin.records;
const INSPECT_ISSUES = PATH_ADMIN.inspectAdmin.issues;
const EXPENSES = PATH_ADMIN.expenses.all;
const VENDORS = PATH_ADMIN.expenses.vendors;

export const OPS_HELPDESK = {
  id: 'ops-helpdesk',
  title: 'Run the service desk',
  summary:
    'Work the ticket queue, and set up the teams and categories that decide '
    + 'where tickets go in the first place.',
  category: 'operations',
  level: 'advanced',
  permission: 'service_requests.manage',
  featureSlug: null,
  estMinutes: 6,
  readinessKey: null,
  keywords: [
    'helpdesk', 'service desk', 'ticket', 'queue', 'assign', 'sla',
    'maintenance request', 'complaint', 'team', 'category',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: SR_TICKETS,
      target: CENTER,
      placement: 'center',
      title: 'This queue is what members judge you on',
      body:
        'Not the coffee and not the furniture. Whether the thing they reported '
        + 'got fixed, and whether anybody told them. Everything raised by a '
        + 'member in the app, or by your own team at the desk, arrives in this '
        + 'one list.',
    },
    {
      id: 'working',
      kind: 'narrate',
      route: SR_TICKETS,
      target: CENTER,
      placement: 'center',
      title: 'Open, assign, comment, close',
      body:
        'An unassigned ticket is nobody\'s ticket, so assign first, even to '
        + 'yourself. Comments are visible to the member, which makes them the '
        + 'cheapest thing you can do: "part ordered, fitting Thursday" turns an '
        + 'angry wait into a reasonable one. Closing is not admin, it is the '
        + 'message that says it is done.',
    },
    {
      id: 'teams',
      kind: 'narrate',
      route: SR_TEAMS,
      target: CENTER,
      placement: 'center',
      title: 'Teams are who gets woken up',
      body:
        'Housekeeping, maintenance, IT, whatever matches how you actually work. '
        + 'Each team has members, and each member can be reached by WhatsApp, by '
        + 'email or both. Get this list right and tickets route themselves. Get '
        + 'it wrong and everything lands on one person who then becomes the '
        + 'bottleneck for the whole building.',
    },
    {
      id: 'categories',
      kind: 'narrate',
      route: SR_CATEGORY,
      target: CENTER,
      placement: 'center',
      title: 'Categories are how a member describes a problem',
      body:
        'Each one points at a team, and that is the whole routing mechanism. '
        + 'Write them in the words a member would use, not in your internal '
        + 'jargon: "AC too cold" rather than "HVAC calibration". Keep the list '
        + 'short. A member facing thirty options picks Other, and Other routes '
        + 'nowhere useful.',
    },
    {
      id: 'rhythm',
      kind: 'narrate',
      route: SR_TICKETS,
      target: CENTER,
      placement: 'center',
      title: 'Then work it on a rhythm',
      body:
        'Sweep the open list at the start and end of each day. Anything older '
        + 'than a few days either needs a decision or an honest message saying '
        + 'when it will happen. Tickets do not go stale because they are hard, '
        + 'they go stale because nobody looked.',
    },
  ],
};

export const OPS_ASSETS = {
  id: 'ops-assets',
  title: 'Track your assets',
  summary:
    'Know what you own, where it is, who has it, and when it is next due for '
    + 'service.',
  category: 'operations',
  level: 'advanced',
  permission: ['assets.view', 'assets.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'asset', 'assets', 'inventory', 'equipment', 'furniture', 'amc',
    'maintenance', 'warranty', 'damage', 'assign asset',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: ASSETS,
      target: CENTER,
      placement: 'center',
      title: 'Everything you own that is worth arguing about',
      body:
        'Chairs, monitors, air conditioners, coffee machines, access readers. '
        + 'The register exists for three moments that all arrive eventually: '
        + 'something breaks and you need its warranty, something goes missing and '
        + 'you need who had it, and somebody asks what this campus is actually '
        + 'worth.',
    },
    {
      id: 'add',
      kind: 'act',
      route: ASSETS,
      target: ANCHOR.assetsAddNew,
      placement: 'bottom',
      title: 'Add one',
      body:
        'Do not try to catalogue the whole building this afternoon. Start with '
        + 'the expensive and the breakable, and add the rest as you touch it.',
      waitingLabel: 'Click to add an asset.',
      advanceOn: [{ type: 'click', target: ANCHOR.assetsAddNew }],
      doItForMe: { type: 'click', target: ANCHOR.assetsAddNew },
      allowSkip: true,
    },
    {
      id: 'fields',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'Location and purchase date do the heavy lifting',
      body:
        'Location is what makes the register usable during an actual problem, so '
        + 'be specific: the room, not the floor. Purchase date and value drive '
        + 'depreciation and tell you when replacing beats repairing. Serial '
        + 'number is what a manufacturer will ask you for on the phone.',
    },
    {
      id: 'assign',
      kind: 'narrate',
      route: ASSETS,
      target: CENTER,
      placement: 'center',
      title: 'Assigning makes someone responsible',
      body:
        'An asset can be assigned to a member, to a team member, or to a space. '
        + 'A monitor lent to a member should be assigned to them, because that is '
        + 'the record you produce at move-out when it is not where it should be. '
        + 'Damage is logged against the asset too, which is what turns a deposit '
        + 'deduction into a conversation rather than a fight.',
    },
    {
      id: 'maintenance',
      kind: 'narrate',
      route: ASSETS_MAINTENANCE,
      target: CENTER,
      placement: 'center',
      title: 'And the point of all of it: nothing surprises you',
      body:
        'Schedule maintenance and record AMC contracts against the asset, and '
        + 'the servicing you keep meaning to book becomes something the system '
        + 'reminds you about. This is how the air conditioning gets serviced in '
        + 'March instead of failing in June with a floor full of members.',
    },
  ],
};

export const OPS_INSPECTIONS = {
  id: 'ops-inspections',
  title: 'Set up inspections',
  summary:
    'Turn the daily walk-round into a checklist somebody completes on a phone, '
    + 'with photos and issues that go somewhere.',
  category: 'operations',
  level: 'advanced',
  permission: ['inspect.view', 'inspect.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'inspection', 'inspect', 'checklist', 'audit', 'walkthrough', 'template',
    'housekeeping', 'snag', 'issue',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: INSPECT_TEMPLATES,
      target: CENTER,
      placement: 'center',
      title: 'The walk-round you already do, written down',
      body:
        'Every operator checks the floor. Almost none of them can prove it '
        + 'happened, or say what was found last Tuesday. Inspections make that '
        + 'routine a checklist your team completes on a phone, with photos, and '
        + 'anything wrong becomes a tracked issue instead of a thing somebody '
        + 'meant to mention.',
    },
    {
      id: 'template',
      kind: 'act',
      route: INSPECT_TEMPLATES,
      target: ANCHOR.inspectNewTemplate,
      placement: 'bottom',
      title: 'A template is the checklist itself',
      body:
        'Start with one you will genuinely run every day, like an opening check '
        + 'of the common areas. A perfect forty point template nobody completes '
        + 'is worth less than a five point one they do.',
      waitingLabel: 'Click New Template.',
      advanceOn: [{ type: 'click', target: ANCHOR.inspectNewTemplate }],
      doItForMe: { type: 'click', target: ANCHOR.inspectNewTemplate },
      allowSkip: true,
    },
    {
      id: 'items',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'Write items as things somebody can answer standing up',
      body:
        '"Meeting room 2 clean and reset" can be answered in three seconds. '
        + '"Assess the state of the facility" cannot be answered at all. Ask for '
        + 'a photo on the items where a photo is the evidence, and let the rest '
        + 'be a yes or no.',
    },
    {
      id: 'run',
      kind: 'narrate',
      route: INSPECT_RECORDS,
      target: CENTER,
      placement: 'center',
      title: 'Every round walked ends up here',
      body:
        'Who ran it, when, and what they answered, kept as a record you can '
        + 'search. Ask it for one tower and three weeks and it rebuilds that '
        + 'month, with the report and the spreadsheet hanging off the answer. '
        + 'That is what you show a landlord, a franchisor or an auditor who '
        + 'wants evidence rather than assurance.',
    },
    {
      id: 'issues',
      kind: 'narrate',
      route: INSPECT_ISSUES,
      target: CENTER,
      placement: 'center',
      title: 'And the failures collect here',
      body:
        'Anything marked wrong during an inspection lands in this list until '
        + 'somebody deals with it. Work it exactly like the service desk queue. '
        + 'An inspection whose failures nobody actions is just a more organised '
        + 'way of ignoring the problem.',
    },
  ],
};

export const OPS_EXPENSES = {
  id: 'ops-expenses',
  title: 'Record what the space costs',
  summary:
    'Log bills against categories and vendors so you can see where the money '
    + 'goes, and so recurring costs stop being a surprise.',
  category: 'operations',
  level: 'advanced',
  permission: ['expenses.view', 'expenses.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'expense', 'expenses', 'bill', 'cost', 'vendor', 'purchase', 'petty cash',
    'recurring', 'consumables', 'supplies',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: EXPENSES,
      target: CENTER,
      placement: 'center',
      title: 'The other half of the P and L',
      body:
        'Invoices tell you what came in. This tells you what went out: rent, '
        + 'power, internet, cleaning, coffee, repairs. Both sides live in DeskOS '
        + 'so the question "did this campus make money last month" has an answer '
        + 'that does not require a spreadsheet.',
    },
    {
      id: 'add',
      kind: 'act',
      route: EXPENSES,
      target: ANCHOR.expensesAddNew,
      placement: 'bottom',
      title: 'Record one',
      body:
        'Log bills as they arrive rather than in a monthly catch-up. The '
        + 'catch-up is where receipts go missing and where the number stops being '
        + 'trustworthy.',
      waitingLabel: 'Click to add an expense.',
      advanceOn: [{ type: 'click', target: ANCHOR.expensesAddNew }],
      doItForMe: { type: 'click', target: ANCHOR.expensesAddNew },
      allowSkip: true,
    },
    {
      id: 'category',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'The category is what makes it a report later',
      body:
        'Categories are how every cost report groups your spending, so a bill '
        + 'filed under the wrong one is worse than useless, it is misleading. '
        + 'Attach the actual bill while you have it, and record the tax '
        + 'separately from the amount rather than lumping them together.',
    },
    {
      id: 'vendors',
      kind: 'narrate',
      route: VENDORS,
      target: CENTER,
      placement: 'center',
      title: 'Vendors turn costs into leverage',
      body:
        'Attach each expense to the vendor it came from and you can see what a '
        + 'supplier has cost you over a year, which is the only number that makes '
        + 'a renegotiation go your way. It also means a disputed bill has a '
        + 'history to point at.',
    },
    {
      id: 'recurring',
      kind: 'narrate',
      route: EXPENSES,
      target: CENTER,
      placement: 'center',
      title: 'And the ones that come back every month',
      body:
        'Rent, internet and cleaning contracts can be set up as recurring so '
        + 'they appear on their own rather than relying on somebody remembering. '
        + 'Consumables, the coffee and paper and soap you buy in bulk and use '
        + 'down, are tracked separately as stock rather than as one-off bills, '
        + 'because those go missing in a different way.',
    },
  ],
};

export default [
  OPS_HELPDESK,
  OPS_ASSETS,
  OPS_INSPECTIONS,
  OPS_EXPENSES,
];
